import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Shield, CheckCircle, XCircle, Loader2, KeyRound } from 'lucide-react'
import { adminApi } from '../lib/api'

type Status = 'loading' | 'ready' | 'verifying' | 'success' | 'error'

export default function MagicLinkVerify() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('loading')
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setError('Invalid magic link')
      return
    }

    validateMagicLink(token)
  }, [token])

  async function validateMagicLink(linkToken: string) {
    try {
      // Call GET endpoint to validate token and get user email
      const response = await adminApi.validateMagicLink(linkToken)

      if (response.success && response.email) {
        setEmail(response.email)
        setStatus('ready')
        // Focus first code input
        setTimeout(() => codeInputRefs.current[0]?.focus(), 100)
      } else {
        setStatus('error')
        setError(response.message || 'Invalid or expired magic link')
      }
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Invalid or expired magic link')
    }
  }

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)

    const newCode = [...code]
    newCode[index] = digit
    setCode(newCode)

    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus()
    }

    if (digit && index === 5) {
      const fullCode = newCode.join('')
      if (fullCode.length === 6) {
        handleVerify(fullCode)
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      const newCode = pastedData.split('')
      setCode(newCode)
      handleVerify(pastedData)
    }
  }

  const handleVerify = async (fullCode?: string) => {
    const codeToVerify = fullCode || code.join('')
    if (codeToVerify.length !== 6) {
      setError('Please enter all 6 digits')
      return
    }

    setError('')
    setStatus('verifying')

    try {
      const response = await adminApi.verifyCode(email, codeToVerify, token!)

      if (response.success && response.token && response.user) {
        adminApi.setToken(response.token)
        localStorage.setItem('admin_user', JSON.stringify(response.user))
        setStatus('success')

        // Redirect to dashboard
        setTimeout(() => {
          window.location.href = '/'
        }, 1500)
      } else {
        setStatus('ready')
        setError(response.message || 'Invalid verification code')
        setCode(['', '', '', '', '', ''])
        codeInputRefs.current[0]?.focus()
      }
    } catch (err) {
      setStatus('ready')
      setError(err instanceof Error ? err.message : 'Verification failed')
      setCode(['', '', '', '', '', ''])
      codeInputRefs.current[0]?.focus()
    }
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary-600 mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">GRC Platform</h1>
          <p className="text-gray-400 mt-2">Central Administration</p>
        </div>

        <div className="card p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 text-primary-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold text-white mb-2">Validating link...</h2>
              <p className="text-gray-400">Please wait while we verify your magic link.</p>
            </>
          )}

          {status === 'ready' && (
            <div className="space-y-6">
              <div>
                <KeyRound className="h-12 w-12 text-primary-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">Enter verification code</h2>
                <p className="text-gray-400 text-sm">
                  Enter the 6-digit code from your email to complete sign in
                </p>
                {email && (
                  <p className="text-primary-400 text-sm mt-2">{email}</p>
                )}
              </div>

              {error && (
                <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
                  {error}
                </div>
              )}

              <div>
                <div className="flex justify-center gap-2" onPaste={handlePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (codeInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-colors"
                    />
                  ))}
                </div>
              </div>

              <button
                onClick={() => handleVerify()}
                disabled={code.join('').length !== 6}
                className="btn-primary w-full"
              >
                Verify & Sign In
              </button>
            </div>
          )}

          {status === 'verifying' && (
            <>
              <Loader2 className="h-12 w-12 text-primary-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold text-white mb-2">Verifying...</h2>
              <p className="text-gray-400">Please wait while we sign you in.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Success!</h2>
              <p className="text-gray-400">You're signed in. Redirecting...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Link Invalid or Expired</h2>
              <p className="text-gray-400 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="btn-primary"
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
